import React, { useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";

export const EnrollForm = ({ open, onClose, onAccept, fileIcon }) => {
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    document.body.classList.add("overflow-hidden");
    return () => document.body.classList.remove("overflow-hidden");
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        ref={panelRef}
        className="w-full max-w-2xl h-[90vh] bg-white rounded-xl shadow-xl grid grid-rows-[auto,1fr,auto] animate-fadeIn"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-white rounded-t-2xl border-b px-3 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div>{fileIcon}</div>
            <span className="text-[#1F2937] font-semibold text-[20px] font-inter">
              Chronic Care Management (CCM) Consent Form
            </span>
          </div>
          <button
            onClick={onClose}
            className="focus:outline-none"
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faXmark} className="text-[#000000] text-[18px]" />
          </button>
        </div>

        <div className="overflow-y-auto px-6 py-6 space-y-6 bg-white rounded-lg shadow-sm">
          <p className="text-sm text-[#374151] text-[14px] font-medium font-inter text-left">
            Please review the following information with the patient:
          </p>

          <section className="space-y-2 text-left">
            <h3 className="font-semibold text-[#1F2937] text-[17px] font-inter">
              What is Chronic Care Management (CCM)?
            </h3>
            <p className="text-[#374151] text-sm leading-6 font-inter font-normal">
              Chronic Care Management is a Medicare program that provides
              non–face-to-face care coordination services to patients with
              two or more chronic conditions. These services are designed to
              help you manage your health conditions more effectively.
            </p>
          </section>

          <section className="space-y-2 text-left">
            <h4 className="font-semibold text-[#1F2937] text-[17px] font-inter">
              Services Included:
            </h4>
            <ul className="list-disc pl-6 text-[#374151] text-sm font-inter font-normal space-y-1">
              <li>24/7 access to care team for urgent needs</li>
              <li>Comprehensive care management and coordination</li>
              <li>Development and management of a personalized care plan</li>
              <li>Medication management and reconciliation</li>
              <li>Communication and coordination with all healthcare providers</li>
              <li>Access to electronic health records</li>
              <li>Regular check-ins and monitoring</li>
            </ul>
          </section>

          <section className="space-y-2 text-left">
            <h4 className="font-semibold text-[#1F2937] text-[17px] font-inter">
              Cost and Billing:
            </h4>
            <p className="text-[#374151] text-sm leading-6 font-inter font-normal">
              Medicare will be billed monthly for these services. If you
              have a secondary insurance, they may also be billed. You may
              be responsible for applicable co-payments, co-insurance, or
              deductibles as determined by your insurance plan.
            </p>
          </section>

          <section className="space-y-2 text-left">
            <h4 className="font-semibold text-[#1F2937] text-[17px] font-inter">
              Your Rights:
            </h4>
            <ul className="list-disc pl-6 text-[#374151] text-sm font-inter font-normal space-y-1">
              <li>You may stop CCM services at any time.</li>
              <li>Only one provider can furnish and bill for CCM services during a calendar month.</li>
              <li>You have the right to privacy under HIPAA regulations.</li>
              <li>You can request a copy of your care plan at any time.</li>
            </ul>
          </section>

          <section className="space-y-2 text-left">
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-3">
              <h4 className="font-semibold text-[#1F2937] text-[17px] font-inter">
                Patient Consent:
              </h4>
              <p className="text-[#374151] text-sm leading-6 font-inter font-normal">
                By accepting below, the patient acknowledges that they have
                been informed about CCM services, understand the potential
                costs, and agree to participate in the program. The patient
                understands they can opt out at any time.
              </p>
            </div>
          </section>
        </div>

        <div className="sticky bottom-0 z-10 bg-[#F9FAFB] rounded-b-2xl border-t border-[#E5E7EB] px-4 py-3 flex gap-3">
          <button
            onClick={onClose}
            className="w-full py-2 rounded-lg border bg-white border-[#D1D5DB] text-[#374151] font-medium transition hover:!bg-[#1D2951] hover:text-[#fff]"
          >
            Cancel
          </button>
          <button
            onClick={onAccept}
            className="w-full py-2 rounded-lg border bg-white border-[#D1D5DB] text-[#374151] font-medium transition hover:!bg-[#1D2951] hover:text-[#fff]"
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
};

export default EnrollForm;
